import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const AppSubContext = createContext(null);

export const AppSubProvider = ({ children }) => {
  const { user } = useAuth();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadedFor, setLoadedFor] = useState(undefined); // user id we've fetched for

  const fetchSub = useCallback(async () => {
    if (!user) {
      setSub(null);
      setLoadedFor(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/app-subscription/me");
      setSub(data.subscription);
    } catch (e) {
      setSub(null);
    } finally {
      setLoadedFor(user.id);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchSub().finally(() => { if (!active) return; });
    return () => { active = false; };
  }, [fetchSub]);

  const refresh = fetchSub;

  // While a user is present but we haven't fetched their subscription yet,
  // keep "loading" true to avoid a premature paywall redirect (race guard).
  const effectiveLoading = loading || (!!user && loadedFor !== user.id);

  return (
    <AppSubContext.Provider value={{ sub, loading: effectiveLoading, refresh }}>
      {children}
    </AppSubContext.Provider>
  );
};

export const useAppSub = () => {
  const ctx = useContext(AppSubContext);
  if (!ctx) return { sub: null, loading: false, refresh: () => {} };
  return ctx;
};
